// Hjälpare för anbudslistan: vyfilter (pågående / avslutade) och visning av
// sista anbudsdag. Hålls fria från React så att de går att testa.

// Avgjorda eller avbrutna anbud. Återdraget och avvisat (vi valde att inte
// lämna in) räknas hit – de är lika avslutade som ett förlorat anbud, även om
// de inte påverkar vinstprocenten.
export const CLOSED_STATUSES = ['won', 'lost', 'declined', 'withdrawn'];
export const ONGOING_STATUSES = ['draft', 'active', 'submitted'];

export const TENDER_VIEWS = [
  { key: 'ongoing', label: 'Pågående' },
  { key: 'closed',  label: 'Avslutade' },
  { key: 'all',     label: 'Alla' },
];

export const isClosed = tender => CLOSED_STATUSES.includes(tender?.status);

export function inView(tender, view) {
  if (view === 'ongoing') return !isClosed(tender);
  if (view === 'closed') return isClosed(tender);
  return true;
}

/** Statusar som är relevanta att filtrera på inom en vy. */
export function statusesForView(view) {
  if (view === 'ongoing') return ONGOING_STATUSES;
  if (view === 'closed') return CLOSED_STATUSES;
  return [...ONGOING_STATUSES, ...CLOSED_STATUSES];
}

const VIEW_KEY = uid => `tendersView_${uid || 'anon'}`;

export function loadView(uid) {
  try {
    const v = localStorage.getItem(VIEW_KEY(uid));
    return TENDER_VIEWS.some(x => x.key === v) ? v : 'ongoing';
  } catch {
    return 'ongoing';
  }
}

export function saveView(uid, view) {
  try { localStorage.setItem(VIEW_KEY(uid), view); } catch { /* privat läge m.m. */ }
}

/**
 * Deadlines lagras som text: 'YYYY-MM-DD HH:MM', 'YYYY-MM-DDTHH:MM' eller
 * bara 'YYYY-MM-DD'. Tolkas som lokal tid. Ett datum utan klockslag gäller
 * hela dagen ut.
 */
export function parseDeadline(str) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?/.exec(String(str || '').trim());
  if (!m) return null;
  const hasTime = m[4] != null;
  const date = new Date(+m[1], +m[2] - 1, +m[3], hasTime ? +m[4] : 23, hasTime ? +m[5] : 59);
  return Number.isNaN(date.getTime()) ? null : { date, hasTime };
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const pad = n => String(n).padStart(2, '0');

/** '2026-10-19 23:59' → '19 okt 23:59'. Annat år än i år får årtalet med. */
export function formatDeadline(str, now = new Date()) {
  const p = parseDeadline(str);
  if (!p) return '';
  const d = p.date;
  const year = d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : '';
  const time = p.hasTime ? ` ${pad(d.getHours())}:${pad(d.getMinutes())}` : '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${year}${time}`;
}

/**
 * Relativ tid till deadline: { text, tone } där tone är 'passed' (röd),
 * 'soon' (≤ 7 dagar, bärnsten) eller 'normal'.
 */
export function relativeDeadline(str, now = new Date()) {
  const p = parseDeadline(str);
  if (!p) return null;
  if (p.date < now) return { text: 'passerad', tone: 'passed' };
  const startOf = d => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(p.date) - startOf(now)) / 86400000);
  const text = days === 0 ? 'i dag' : days === 1 ? 'i morgon' : `om ${days} dagar`;
  return { text, tone: days <= 7 ? 'soon' : 'normal' };
}

/**
 * Anbudets status utifrån fasernas utfall. En förlorad fas avslutar hela
 * anbudet; vunnen sista fas betyder vunnet anbud. Annars ligger anbudet kvar
 * som det var – utom när det tidigare stod som avgjort, då det backar till
 * inlämnat (utfallet har återställts).
 */
export function statusFromPhaseOutcomes(phases, currentStatus) {
  const list = phases || [];
  if (list.some(p => p?.outcome === 'lost')) return 'lost';
  if (list.length && list[list.length - 1]?.outcome === 'won') return 'won';
  if (currentStatus === 'won' || currentStatus === 'lost') return 'submitted';
  return currentStatus || 'submitted';
}
